import React, { useState, useEffect } from "react";
import { Button } from "../../../../../components/ui/button";
import { Tooltip } from "../../../../../components/ui/tooltip";
import { Checkbox } from "../../../../../components/ui/checkbox";
import { Input } from "../../../../../components/ui/input";
import { Info, Plus, Save, ArrowLeft, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../../../../components/ui/dialog";
import { Label } from "../../../../../components/ui/label";
import { Notification as customNotification } from "../../../../../components/ui/custom-notification";
import { DeleteConfirmationDialog } from "../../../../../components/ui/DeleteConfirmationDialog";
import { useTeamMembersReduxHook } from "../../../hooks/useTeamMembersRedux";

// --- Types
const PERMISSIONS = ["View", "Edit", "Report", "Sign", "Manage"] as const;
type Permission = typeof PERMISSIONS[number];

// Features removed - using roles directly

// Default roles - can be extended
const DEFAULT_ROLES = [
  "Project Manager",
  "Welder",
  "QA/QC",
  "Customer Representative",
  "Material Manager – Coating",
  "Material Manager – Pipe",
  "Technician",
  "Others",
] as const;

type Role = string;

// Matrix state: role -> permission -> boolean
type Matrix = Record<Role, Record<Permission, boolean>>;

// Default matrix generator
const defaultMatrix = (roles: Role[]): Matrix => {
  const m: Partial<Matrix> = {};
  roles.forEach((r) => {
    // baseline defaults: viewers for all; editors only for relevant roles; sign for PM + QA/QC + Customer Rep; manage for PM
    const base: Record<Permission, boolean> = {
      View: true,
      Edit: ["Project Manager", "Welder", "Technician", "Material Manager – Coating", "Material Manager – Pipe"].includes(r),
      Report: ["Project Manager", "QA/QC", "Customer Representative"].includes(r),
      Sign: ["Project Manager", "QA/QC", "Customer Representative"].includes(r),
      Manage: r === "Project Manager",
    };
    m[r] = base;
  });
  return m as Matrix;
};

interface RoleAccessMatrixProps {
  onBack?: () => void;
  projectId?: string | number;
  isOpen?: boolean;
}

export default function RoleAccessMatrix({ onBack, projectId, isOpen = true }: RoleAccessMatrixProps) {
  const {
    projectRoles,
    rolesLoading,
    fetchProjectRoles,
    createUpdateProjectLevelRoles,
    createUpdateRolesLoading,
    deleteProjectLevelRole,
    deleteRoleLoading,
    error
  } = useTeamMembersReduxHook(undefined, projectId);
  
  const [saveSuccess, setSaveSuccess] = useState(false);
  const hasFetchedRef = React.useRef(false);

  const [roles, setRoles] = useState<Role[]>([...DEFAULT_ROLES]);
  const [matrix, setMatrix] = useState<Matrix>(() => defaultMatrix([...DEFAULT_ROLES]));
  const [isAddingRole, setIsAddingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [defaultCheckStates, setDefaultCheckStates] = useState<Record<Permission, boolean>>({
    View: true,
    Edit: false,
    Report: false,
    Sign: false,
    Manage: false,
  });
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<{ roleName: Role; roleId?: number | string } | null>(null);

  // Fetch project roles only when modal opens (once per modal open)
  useEffect(() => {
    if (isOpen && projectId && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchProjectRoles({ projectId });
    }
    // Reset ref when modal closes
    if (!isOpen) {
      hasFetchedRef.current = false;
    }
  }, [isOpen, projectId, fetchProjectRoles]);

  // Load data from API response into matrix
  useEffect(() => {
    console.log("projectRoles", projectRoles);
    if (projectRoles && projectRoles.length > 0) {
      const loadedRoles: Role[] = [];
      const loadedMatrix: Partial<Matrix> = {};

      // Extract roles from the nested structure: projectRoles[0].projectRolePermissions.roles
      const firstEntry = projectRoles[0];
      const rolesArray = firstEntry?.projectRolePermissions?.roles || [];

      rolesArray.forEach((role: any) => {
        const roleName = role.projectRoleName || role.project_role_name;
        if (roleName) {
          loadedRoles.push(roleName);
          
          // Map permissions from API to matrix format
          const permissions = role.permissions || {};
          const projectPermissions = permissions.project || [];
          
          // Map API permission names to our permission format
          // API uses lowercase: read, write, report, sign, manage
          loadedMatrix[roleName] = {
            View: projectPermissions.includes('read') || projectPermissions.includes('Read') || true, // Default to true
            Edit: projectPermissions.includes('write') || projectPermissions.includes('Write') || projectPermissions.includes('edit') || projectPermissions.includes('Edit'),
            Report: projectPermissions.includes('report') || projectPermissions.includes('Report'),
            Sign: projectPermissions.includes('sign') || projectPermissions.includes('Sign'),
            Manage: projectPermissions.includes('manage') || projectPermissions.includes('Manage'),
          };
        }
      });

      // Update roles and matrix even if empty (to clear deleted roles)
      setRoles(loadedRoles);
      setMatrix(loadedMatrix as Matrix);
    }
    // else {
    //     // Load default roles when no project roles exist, but only if projectId is available
    //     if (projectId) {
    //       setRoles([...DEFAULT_ROLES]);
    //       setMatrix(defaultMatrix([...DEFAULT_ROLES]));
    //     }
    // }
  }, [projectRoles, projectId]);

  // Listen for save success/error
  useEffect(() => {
    if (!createUpdateRolesLoading && saveSuccess) {
      // Fetch the updated data from API to ensure we have the latest saved permissions
      if (projectId) {
        fetchProjectRoles({ projectId });
      }
      customNotification.success("Saved!", "Role permissions have been saved successfully.");
      setSaveSuccess(false);
    }
  }, [createUpdateRolesLoading, saveSuccess, projectId, fetchProjectRoles]);

  // Listen for errors
  useEffect(() => {
    if (error && typeof error === 'object' && error.message) {
      const errorMessage = error.message;
      if (errorMessage.includes('project level roles') || errorMessage.includes('CREATE_UPDATE')) {
        customNotification.error("Save Failed", errorMessage);
      }
    }
  }, [error]);

  // Update matrix when roles change
  React.useEffect(() => {
    const currentRoles = Object.keys(matrix);
    const newRoles = roles.filter(r => !currentRoles.includes(r));
    
    if (newRoles.length > 0) {
      setMatrix((prev) => {
        const updated = { ...prev };
        newRoles.forEach((role) => {
          updated[role] = { ...defaultCheckStates };
        });
        return updated;
      });
    }
  }, [roles, defaultCheckStates]);

  const toggle = (role: Role, perm: Permission) => {
    setMatrix((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [perm]: !prev[role][perm],
      },
    }));
  };

  const setAllPermissionsForRole = (role: Role, value: boolean) => {
    setMatrix((prev) => ({
      ...prev,
      [role]: PERMISSIONS.reduce((acc, perm) => ({ ...acc, [perm]: value }), {} as Record<Permission, boolean>),
    }));
  };

  const setPermissionForAllRoles = (permission: Permission, value: boolean) => {
    setMatrix((prev) => {
      const updated = { ...prev };
      roles.forEach(role => {
        updated[role] = {
          ...updated[role],
          [permission]: value,
        };
      });
      return updated;
    });
  };

  const resetAll = () => {
    const d = defaultMatrix(roles);
    setMatrix(d);
  };

  const addNewRole = () => {
    if (newRoleName.trim() && !roles.includes(newRoleName.trim())) {
      const roleToAdd = newRoleName.trim();
      setRoles((prev) => [...prev, roleToAdd]);
      
      // Add to matrix with default check states
      setMatrix((prev) => ({
        ...prev,
        [roleToAdd]: { ...defaultCheckStates },
      }));
      
      setNewRoleName("");
      setIsAddingRole(false);
      console.log("Role added:", roleToAdd);
      customNotification.success("Role Added", `Role "${roleToAdd}" has been added successfully.`);
    } else if (roles.includes(newRoleName.trim())) {
      customNotification.warning("Duplicate Role", "This role already exists.");
    } else {
      customNotification.warning("Invalid Role", "Please enter a valid role name.");
    }
  };

  const removeRole = (roleToRemove: Role) => {
    if (roles.length <= 1) {
      customNotification.warning("Cannot Remove", "At least one role must exist.");
      return;
    }
    
    setRoles((prev) => prev.filter((r) => r !== roleToRemove));
    setMatrix((prev) => {
      const { [roleToRemove]: removed, ...rest } = prev;
      return rest;
    });
    customNotification.success("Role Removed", `Role "${roleToRemove}" has been removed.`);
  };

  const handleDeleteRole = (roleName: Role) => {
    // Find the role data to get the ID from nested structure
    const firstEntry = projectRoles?.[0];
    const rolesArray = firstEntry?.projectRolePermissions?.roles || [];
    const roleData = rolesArray.find((r: any) => 
      (r.projectRoleName || r.project_role_name) === roleName
    );

    // Get projectLevelRoleId from the parent entry
    const roleId = firstEntry?.projectLevelRoleId || firstEntry?.project_level_role_id;
    
    // Set the role to delete and show confirmation dialog
    setRoleToDelete({ roleName, roleId });
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = () => {
    if (!roleToDelete) {
      setIsDeleteModalVisible(false);
      setRoleToDelete(null);
      return;
    }

    // If it's a new role that hasn't been saved yet, just remove it locally
    if (!roleToDelete.roleId) {
      removeRole(roleToDelete.roleName);
      setIsDeleteModalVisible(false);
      setRoleToDelete(null);
      return;
    }

    // If no projectId, show warning but still close the dialog
    if (!projectId) {
      customNotification.warning("Missing Project ID", "Please provide a project ID to delete roles.");
      setIsDeleteModalVisible(false);
      setRoleToDelete(null);
      return;
    }

    // Delete the role via API
    deleteProjectLevelRole({ roleId: roleToDelete.roleId, projectId });
    setIsDeleteModalVisible(false);
    setRoleToDelete(null);
  };

  const handleSave = () => {
    if (!projectId) {
      customNotification.warning("Missing Project ID", "Please provide a project ID to save roles.");
      return;
    }
    console.log("Roles:", roles);
    
    // Get existing projectLevelRoleId if updating
    const firstEntry = projectRoles?.[0];
    const existingProjectLevelRoleId = firstEntry?.projectLevelRoleId || firstEntry?.project_level_role_id;

    // Transform matrix data to API payload format
    const rolesPayload = roles.map((roleName) => {
      const rolePermissions = matrix[roleName] || {};
      const apiPermissions: string[] = [];
      
      // Map our permissions to API format (lowercase)
      if (rolePermissions.View) apiPermissions.push('read');
      if (rolePermissions.Edit) apiPermissions.push('write');
      if (rolePermissions.Report) apiPermissions.push('report');
      if (rolePermissions.Sign) apiPermissions.push('sign');
      if (rolePermissions.Manage) apiPermissions.push('manage');

      const roleObj: any = {
        projectRoleName: roleName,
        permissions: {
          project: apiPermissions
        }
      };

      return roleObj;
    });

    // Create payload matching API structure: array with single object
    const payload = {
      projectId: projectId,
      projectRolePermissions: {
        roles: rolesPayload
      },
      isActive: true,
      ...(existingProjectLevelRoleId && { projectLevelRoleId: existingProjectLevelRoleId })
    };

    console.log("Save payload:", payload);
    
    // Call the API
    createUpdateProjectLevelRoles(payload);
    setSaveSuccess(true);
    
    // Note: Success notification will be shown after API call completes
    // Error notification will be handled by the error useEffect
  };

  // Check if all roles have a permission
  const allRolesHavePermission = (permission: Permission): boolean => {
    return roles.every(role => matrix[role]?.[permission]);
  };

  // Check if all permissions are checked for a role
  const allPermissionsForRole = (role: Role): boolean => {
    return PERMISSIONS.every(perm => matrix[role]?.[perm]);
  };

  const headerHelp: Record<Permission, string> = {
    View: "Read-only access to data in this feature.",
    Edit: "Create/update/delete entries in this feature.",
    Report: "Generate/export reports (PDF/CSV) for this feature.",
    Sign: "Digitally sign or approve records/reports.",
    Manage: "Invite users to this project and adjust this feature's access matrix.",
  };


  const FeatureTable = () => (
    <div className="border border-[#E2E5EA] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-page-bg border-b border-[#EEF0F3] sticky top-0 z-20">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-text-grey sticky left-0 top-0 bg-page-bg z-30 min-w-[200px]">
                Role
              </th>
              {PERMISSIONS.map((permission) => {
                const allChecked = allRolesHavePermission(permission);

                return (
                  <th key={permission} className="px-3 py-3 text-center font-semibold text-text-grey relative">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-1">
                        <span>{permission}</span>
                        <Tooltip
                          content={<p className="max-w-xs text-[11px]">{headerHelp[permission]}</p>}
                          placement="bottom"
                          trigger="click"
                        >
                          <Info className="h-3 w-3 text-heritage-grey cursor-pointer hover:text-text-grey transition-colors" />
                        </Tooltip>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={allChecked}
                          onCheckedChange={(checked) => setPermissionForAllRoles(permission, checked === true)}
                          className="h-4 w-4"
                        />
                        <span className="text-[11px] text-heritage-grey">All</span>
                      </div>
                    </div>
                  </th>
                );
              })}
              <th className="px-4 py-3 text-left font-semibold text-text-grey">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role, index) => (
              <tr key={role} className={index % 2 === 0 ? 'bg-white' : 'bg-page-bg/50'}>
                <td className="px-4 py-3 font-medium text-text-grey sticky left-0 bg-inherit z-10">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={allPermissionsForRole(role)}
                      onCheckedChange={(checked) => {
                        setAllPermissionsForRole(role, checked === true);
                      }}
                      className="h-4 w-4"
                    />
                    <span>{role}</span>
                  </div>
                </td>
                {PERMISSIONS.map((permission) => (
                  <td key={permission} className="px-3 py-3 text-center">
                    <Checkbox
                      checked={matrix[role]?.[permission] || false}
                      onCheckedChange={() => toggle(role, permission)}
                      className="h-4 w-4"
                    />
                  </td>
                ))}
                <td className="px-4 py-3 text-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteRole(role)}
                    disabled={deleteRoleLoading}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 w-8 h-8 p-0 flex items-center justify-center"
                    title="Delete role"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const handleClose = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-hidden flex flex-col bg-page-bg">
        <DialogHeader className="flex-shrink-0 px-6 py-4 pr-14 border-b border-[#E2E5EA] bg-white flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={handleClose}
              variant="outline"
              size="sm"
              className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
            >
              <ArrowLeft className="h-4 w-4 text-text-grey" />
            </Button>
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              Project Role-Based Access
            </DialogTitle>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={isAddingRole} onOpenChange={setIsAddingRole}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-4 border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Role
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">Add New Role</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="roleName" className="text-[12px] font-medium text-text-grey">Role Name</Label>
                    <Input
                      id="roleName"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      placeholder="Enter role name"
                      className="h-10 text-[13px]"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          addNewRole();
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label className="text-[12px] font-medium text-text-grey">Default Permissions</Label>
                    <div className="space-y-2 mt-2">
                      {PERMISSIONS.map((perm) => (
                        <div key={perm} className="flex items-center space-x-2">
                          <Checkbox
                            checked={defaultCheckStates[perm]}
                            onCheckedChange={(checked) =>
                              setDefaultCheckStates((prev) => ({
                                ...prev,
                                [perm]: Boolean(checked),
                              }))
                            }
                          />
                          <Label className="text-[13px] font-normal text-text-grey">{perm}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAddingRole(false)}
                      className="h-9 px-4 border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={addNewRole}
                      size="sm"
                      className="h-9 px-4 bg-hero-red text-white hover:bg-hero-red-60"
                    >
                      Add Role
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Button
              onClick={handleSave}
              disabled={createUpdateRolesLoading || !projectId}
              size="sm"
              className="h-9 px-4 bg-hero-red text-white hover:bg-hero-red-60"
            >
              <Save className="mr-2 h-4 w-4" />
              {createUpdateRolesLoading ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          <FeatureTable />
        </div>

        <DeleteConfirmationDialog
          isOpen={isDeleteModalVisible}
          onClose={() => {
            setIsDeleteModalVisible(false);
            setRoleToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          itemType="Role"
          itemName={roleToDelete?.roleName}
          isLoading={deleteRoleLoading}
        />
      </DialogContent>
    </Dialog>
  );
}

